"use client"

import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { User } from "../../../lib/types"
import { Users, X, Check, SaveIcon } from "lucide-react"
import { Category, Project } from "../../../lib/types"

interface SelectUsersModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  allUsers: User[]
  selectedUserIds: string[]
  currentCategory: Category
  onConfirm: (userIds: string[]) => void
}

export function SelectUsersModal({
  open,
  onOpenChange,
  allUsers,
  selectedUserIds,
  currentCategory,
  onConfirm,
}: SelectUsersModalProps) {
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedUserIds)
    }
  }, [open, selectedUserIds])

  const handleToggle = (userId: string) => {
    setTempSelectedIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleConfirm = () => {
    onConfirm(tempSelectedIds)
    onOpenChange(false)
  }

  const availableUsers = allUsers;/*.filter(
    (user) => !selectedUserIds.includes(user.id)
  )*/

  const newSelectionsCount = tempSelectedIds.length - selectedUserIds.length

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4 w-full">
        <div>
          <h1 className="text-xl font-semibold">Selecionar Usuários</h1>
          <p className="text-sm text-muted-foreground w-[80%]">
            Marque os usuários que deseja compartilhar essa categoria. Se o usuário não aparecer aqui é por que não tem os dados cadastrados no simulador. 
            Peça a loja para preencher os campo na tela de projetos do simulador no botão [dados da loja].
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={newSelectionsCount === 0}
          >
            <SaveIcon className="h-4 w-4 mr-2"></SaveIcon>
            Salvar {newSelectionsCount > 0 && `(${newSelectionsCount})`}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        {availableUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Users className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              Todos os usuários já foram adicionados.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {availableUsers.map((user) => {
              const isSelected = tempSelectedIds.includes(user.id)

              return (
                <button
                  type="button"
                  key={user.id}
                  onClick={() => handleToggle(user.id)}
                  className={`group relative rounded-lg border-2 overflow-hidden text-left transition-all ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-transparent hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="relative aspect-square flex items-center justify-center bg-muted">
                    <img
                      src={user.avatar || "/avatar-placeholder.png"}
                      alt={user.name}
                      className="h-full w-full object-contain absolute"
                    />
                    <p className="font-medium text-sm line-clamp-1 top-[10px] absolute bg-secondary">
                      {user.name}
                    </p>
                    <div
                      className={`absolute inset-0 bg-primary/10 transition-opacity ${
                        isSelected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={isSelected}
                        className="h-5 w-5 border-2 bg-background/80 backdrop-blur-sm"
                      />
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}